/**
 * The exit half of the motion system (PO, 2026-09-25: every layer over the page animates on open AND on close).
 *
 * A layer that closes is marked `data-state="closed"` and left in the page until its own exit animation ends
 * (`animationend` on the layer itself, not a child), or until the animation's length plus 50 ms if that event
 * never comes (a tab in the background, an engine that drops it). Where nothing animates — jsdom, which loads no
 * stylesheet, or an animation the stylesheet does not give this state — it goes at once, so a test that expects a
 * closed dialog to be gone still finds it gone.
 *
 * The keyframes live in `styles/motion.css`; nothing here writes a style, so `style-src 'self'` is untouched.
 * Two consumers: `usePresence` (hooks/use-presence.ts), for a layer that owns its open state, and ModalDialog's
 * `lingerOnUnmount`, for a dialog its caller unmounts.
 */

export const EXIT_FALLBACK_PAD_MS = 50;

const seconds = (value: string): number[] => value.split(',').map((part) => {
  const text = part.trim();
  const n = Number.parseFloat(text);
  if (!Number.isFinite(n)) return 0;
  return text.endsWith('ms') ? n : n * 1000;
});

/**
 * How long the element's own CSS animation runs, delay included, in ms; `null` when it has none. Read from the
 * computed style, so it is the one the stylesheet picked for this state, breakpoint and motion preference.
 */
export function animationLength(el: Element): number | null {
  const style = getComputedStyle(el);
  const names = (style.animationName ?? '').split(',').map((name) => name.trim());
  if (names.every((name) => name === '' || name === 'none')) return null;
  const durations = seconds(style.animationDuration ?? '0s');
  const delays = seconds(style.animationDelay ?? '0s');
  return Math.max(0, ...names.map((_, i) =>
    (durations[i % durations.length] ?? 0) + (delays[i % delays.length] ?? 0)));
}

/**
 * Calls `done` once the element's exit animation is over: on its own `animationend` (or `animationcancel`), or
 * at the animation's length plus `EXIT_FALLBACK_PAD_MS`. With no animation, `done` runs before this returns.
 * Returns a cancel, for a layer reopened before it has gone.
 */
export function afterExit(el: HTMLElement, done: () => void): () => void {
  const length = animationLength(el);
  // An engine that can list the element's animations has the last word: a named animation it is not running
  // (the element is `display: none`, say) will never send `animationend`.
  const running = typeof el.getAnimations !== 'function' || el.getAnimations().length > 0;
  if (length === null || !running) {
    done();
    return () => {};
  }
  let settled = false;
  const onEnd = (event: Event) => { if (event.target === el) finish(); };
  const timer = window.setTimeout(finish, length + EXIT_FALLBACK_PAD_MS);
  el.addEventListener('animationend', onEnd);
  el.addEventListener('animationcancel', onEnd);
  function stop() {
    settled = true;
    window.clearTimeout(timer);
    el.removeEventListener('animationend', onEnd);
    el.removeEventListener('animationcancel', onEnd);
  }
  function finish() {
    if (settled) return;
    stop();
    done();
  }
  return () => { if (!settled) stop(); };
}

/**
 * How far an exit that started at `startedAt` (a `performance.now()`) had got, 0 to 1, for a layer that opens
 * again before it has gone: `resumeEntry` starts the entry that far from its end, so the layer turns round where
 * it is instead of blinking out and fading in from nothing.
 */
export function exitProgress(startedAt: number, length: number): number {
  if (length <= 0) return 1;
  return Math.min(1, Math.max(0, (performance.now() - startedAt) / length));
}

/**
 * A layer its caller unmounts — ModalDialog, rendered as `{open && <ModalDialog>}` all over the product — cannot
 * stay mounted, so its own nodes stay instead. `lingerOnUnmount` is called from the dialog's layout-effect cleanup,
 * while the scrim is still in the page: it marks it `data-state="closed"` and, once React's commit has taken it
 * out (a microtask later, before the next paint), puts the same nodes back where they were — typed text, scroll
 * positions and all — as a still picture that is:
 *
 *   - `inert` and `aria-hidden`: no focus, no click, no screen reader, and no React handler can fire from it;
 *   - no longer `aria-modal`, so the shell's shortcuts and every open dialog's Escape and Tab rules ignore it;
 *   - stripped of its ids, so a dialog opened next finds its own `aria-labelledby` and labels, not the leaving one's.
 *
 * It goes on its scrim's `animationend`, or at the animation's length plus 50 ms. With no animation, or when the
 * place it stood in went with it, it is never put back. A dialog nested in another that is leaving too stays
 * inside that one's picture and fades with it.
 */
type Leaving = { el: HTMLElement; key: string; at: number; length: number; end: () => void };
const leaving = new Set<Leaving>();
/** A cleanup that StrictMode's development double-mount undoes before the microtask runs. */
const undone = new WeakMap<HTMLElement, () => void>();

export function lingerOnUnmount(el: HTMLElement, key: string): void {
  const parent = el.parentNode;
  const next = el.nextSibling;
  el.setAttribute('data-state', 'closed');
  const length = animationLength(el);
  if (length === null || parent === null) return;
  let cancelled = false;
  undone.set(el, () => {
    cancelled = true;
    el.setAttribute('data-state', 'open');
  });
  void Promise.resolve().then(() => {
    undone.delete(el);
    // Still in the page: StrictMode's remount took it back, or it is inside an outer dialog's leaving picture.
    if (cancelled || el.isConnected) return;
    // Its place went with it (the screen under it was left): there is nothing to fade over.
    if (!parent.isConnected) return;
    el.remove();
    parent.insertBefore(el, next !== null && next.parentNode === parent ? next : null);
    el.setAttribute('inert', '');
    el.setAttribute('aria-hidden', 'true');
    for (const modal of el.querySelectorAll('[aria-modal]')) modal.removeAttribute('aria-modal');
    for (const named of [el, ...el.querySelectorAll('[id]')]) named.removeAttribute('id');
    const entry: Leaving = { el, key, at: performance.now(), length, end: () => {} };
    leaving.add(entry);
    const stop = afterExit(el, () => {
      leaving.delete(entry);
      el.remove();
    });
    entry.end = () => {
      stop();
      leaving.delete(entry);
      el.remove();
    };
  });
}

/**
 * Called when a dialog mounts, from its layout effect: undoes a StrictMode cleanup of this same element, and
 * takes away at once any dialog of the same kind (`key`) still leaving from the same place — a reopen is one
 * dialog, never a leaving one under an arriving one. Returns how far that exit had got (1 when there was none),
 * for `resumeEntry`.
 */
export function replaceLeaving(el: HTMLElement, key: string): number {
  const undo = undone.get(el);
  undone.delete(el);
  undo?.();
  let progress = 1;
  for (const entry of [...leaving]) {
    if (entry.key !== key || entry.el.parentNode !== el.parentNode) continue;
    progress = Math.min(progress, exitProgress(entry.at, entry.length));
    entry.end();
  }
  return progress;
}

/** Moves the entry animations of `els` on by `1 - progress` of their length (see `exitProgress`). */
export function resumeEntry(els: readonly (Element | null | undefined)[], progress: number): void {
  if (progress >= 1) return;
  for (const el of els) {
    if (!el || typeof el.getAnimations !== 'function') continue;
    for (const animation of el.getAnimations()) {
      const duration = Number(animation.effect?.getTiming().duration ?? 0);
      if (Number.isFinite(duration) && duration > 0) animation.currentTime = (1 - progress) * duration;
    }
  }
}
